/**
 * Invite management service
 */
import { devError } from '../utils/devLog';
import api from './api';

export interface Invite {
  id: string;
  code: string;
  created_at: string;
  used_at?: string;
  is_active: boolean;
  invite_url: string;
  used_by?: {
    id: string;
    username: string;
    email: string;
  };
}

export interface InviteListResponse {
  invites: Invite[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
  available_count: number;
}

export interface GenerateInviteResponse {
  invite: Invite;
  remaining_invites: number;
}

export interface InviteValidationResponse {
  valid: boolean;
  invite?: {
    code: string;
    is_active: boolean;
  };
}

class InviteService {
  /**
   * Get user's invites
   */
  async getMyInvites(
    status: 'all' | 'available' | 'used' = 'all',
    page: number = 1,
    limit: number = 25
  ): Promise<InviteListResponse> {
    const response = await api.get<InviteListResponse>('/invites/my', {
      params: { status, page, limit },
    });
    return response.data;
  }

  /**
   * Generate new invite code
   */
  async generateInvite(): Promise<GenerateInviteResponse> {
    const response = await api.post<GenerateInviteResponse>('/invites/generate');
    return response.data;
  }

  /**
   * Validate invite code (public endpoint)
   */
  async validateInvite(code: string): Promise<InviteValidationResponse> {
    // Normalize code: remove whitespace and convert to uppercase
    const normalizedCode = code.replace(/\s/g, '').toUpperCase();
    const response = await api.get<InviteValidationResponse>(`/invites/validate/${normalizedCode}`);
    return response.data;
  }

  /**
   * Copy invite URL to clipboard
   */
  async copyInviteUrl(code: string): Promise<boolean> {
    const url = `${window.location.origin}/register?invite=${code}`;
    try {
      await navigator.clipboard.writeText(url);
      return true;
    } catch (error) {
      devError('Failed to copy invite URL:', error);
      return false;
    }
  }

  /**
   * Copy invite code to clipboard
   */
  async copyInviteCode(code: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(code);
      return true;
    } catch (error) {
      devError('Failed to copy invite code:', error);
      return false;
    }
  }
}

export const inviteService = new InviteService();
export default inviteService;
